import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retryWhen } from 'rxjs';

import { IHttpErrorResponseObject } from '@shared-interfaces/error';

import { environment } from '../../environments/environment';
import { MessageService } from '../services/message.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private readonly messageService: MessageService) {}

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    /**@todo This should be move to a separate interceptor since it has nothing to do with error logging. It also is not secure as just checking th URL is not a real for of authorization! */
    //This isn't attempting to authorize anyone. All this does, is send attempt to send the session cooke and auth headers if the url is the webserver url. Sending credentials to external sites
    //like timbertrap, cause CORS errors.
    const clonedRequest = request.clone({
      withCredentials: request.url.includes(environment.webServerUrl),
    });

    return next.handle(clonedRequest).pipe(
      retryWhen(this.genericRetryStrategy()),
      catchError((error: HttpErrorResponse) => {
        const code = this.getCode(error);
        if (clonedRequest.url.includes(environment.timberTrapUrl)) return throwError(() => error);
        if (code === '401') return this.handleUnauthorized$();
        if (code === '503') return this.handleOutageMode$();

        return this.catchAll$(code, clonedRequest.url, error);
      }),
    );
  }

  private getCode(error: HttpErrorResponse): string {
    let code: string | number = '0';
    const serverError: IHttpErrorResponseObject = error.error;
    if (serverError.code) code = serverError.code;
    else if (serverError.status) code = serverError.status;
    else if (error.status) code = error.status;
    return code.toString();
  }

  private getMessage(error: HttpErrorResponse): string {
    const serverError = error.error as IHttpErrorResponseObject | Error | undefined;
    if (this.isNestError(serverError)) {
      if (serverError.friendlyMessage !== '') return serverError.friendlyMessage;
      if (serverError.technicalMessage !== '') return serverError.technicalMessage;
    } else if (serverError?.message != null) {
      return serverError.message;
    }
    return error.message;
  }

  private isNestError(error: IHttpErrorResponseObject | Error | undefined): error is IHttpErrorResponseObject {
    return error != null && Object.hasOwn(error, 'friendlyMessage');
  }

  private catchAll$(code: string, url: string, error: HttpErrorResponse): Observable<never> {
    const serverError: IHttpErrorResponseObject = error.error;
    let msg = this.getMessage(error);
    if (code === '0') msg = `A connection error occurred, please try again`;

    this.messageService.error(msg, 'Error', serverError, url);
    return throwError(() => new Error(msg, { cause: serverError }));
  }

  private handleUnauthorized$(): Observable<never> {
    window.location.href = `${environment.webServerUrl}login/sso?RelayState=${environment.application}`;
    //not actually an error condition, but we have to return an observable
    return throwError(() => new Error('Unauthorized! Redirecting to login...'));
  }

  private handleOutageMode$(): Observable<never> {
    window.location.href = environment.webServerUrl;
    //not actually an error condition, but we have to return an observable
    return throwError(() => new Error('Outage Mode! Redirecting to landing page...'));
  }

  private genericRetryStrategy(): (response: Observable<HttpErrorResponse>) => Observable<number> {
    const maxRetryAttempts = 1;
    const scalingDuration = 200;
    const excludedStatusCodes = [400, 401, 403, 404, 503]; //don't retry if these error codes are encountered

    return (attempts$: Observable<HttpErrorResponse>) => {
      return attempts$.pipe(
        mergeMap((error, i) => {
          const retryAttempt = i + 1;
          if (retryAttempt > maxRetryAttempts || excludedStatusCodes.includes(error.status)) {
            return throwError(() => error);
          }
          console.info(`Attempt ${retryAttempt.toString()}: retrying in ${(retryAttempt * scalingDuration).toString()}ms`);
          // retry after 1s, 2s, etc...
          return timer(retryAttempt * scalingDuration);
        }),
      );
    };
  }
}
